import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtTRY, fmtDate, todayISO } from "@/lib/format";
import { toast } from "sonner";
import { Plus, CheckCircle2, Trash2, X } from "lucide-react";

const RECUR = { daily: "Günlük", weekly: "Haftalık", monthly: "Aylık", yearly: "Yıllık" };
const PAYMETHOD = {
  bank_transfer: "Havale/EFT",
  cash: "Nakit",
  cheque: "Çek",
  credit_card: "Kredi Kartı",
  other: "Diğer",
};

export default function Upcoming() {
  const [items, setItems] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    const [{ data: u }, { data: a }, { data: c }] = await Promise.all([
      api.get("/upcoming"), api.get("/accounts"), api.get("/contacts"),
    ]);
    setItems(u); setAccounts(a); setContacts(c);
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = items.filter((u) => filter === "all" ? u.status !== "paid" : u.kind === filter);

  const markPaid = async (id) => {
    await api.post(`/upcoming/${id}/mark-paid`);
    toast.success("Ödendi olarak işaretlendi");
    load();
  };
  const remove = async (id) => {
    if (!window.confirm("Silinsin mi?")) return;
    await api.delete(`/upcoming/${id}`); load();
  };

  const totalPay = items.filter((u) => u.kind === "payment" && u.status !== "paid").reduce((s, u) => s + u.amount, 0);
  const totalCol = items.filter((u) => u.kind === "collection" && u.status !== "paid").reduce((s, u) => s + u.amount, 0);

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="label-caps">Ödemeler & Tahsilatlar</p>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-2">Yaklaşan</h1>
        </div>
        <button data-testid="upcoming-add-button" onClick={() => setOpen(true)}
                className="brutal-btn bg-black text-white px-4 py-2.5 text-sm flex items-center gap-2">
          <Plus size={16} /> Yeni Kayıt
        </button>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="brutal-card p-5">
          <div className="label-caps">Yaklaşan Ödemeler</div>
          <div className="font-mono-num text-3xl mt-2 text-rose-600">{fmtTRY(totalPay)}</div>
        </div>
        <div className="brutal-card p-5">
          <div className="label-caps">Yaklaşan Tahsilatlar</div>
          <div className="font-mono-num text-3xl mt-2 text-emerald-700">{fmtTRY(totalCol)}</div>
        </div>
      </div>

      <div className="flex gap-2">
        {[["all", "Bekleyenler"], ["payment", "Ödemeler"], ["collection", "Tahsilatlar"]].map(([k, l]) => (
          <button key={k} data-testid={`filter-${k}`} onClick={() => setFilter(k)}
                  className={`px-4 py-1.5 text-xs uppercase tracking-widest border ${filter === k ? "bg-black text-white border-black" : "border-black/20"}`}>
            {l}
          </button>
        ))}
      </div>

      <div className="brutal-card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b-2 border-black/80">
              <th className="text-left px-4 py-3 label-caps">Vade</th>
              <th className="text-left px-4 py-3 label-caps">Başlık</th>
              <th className="text-left px-4 py-3 label-caps">Tür</th>
              <th className="text-left px-4 py-3 label-caps">Cari</th>
              <th className="text-left px-4 py-3 label-caps">Durum</th>
              <th className="text-right px-4 py-3 label-caps">Tutar</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="text-center py-10 text-neutral-500">Kayıt yok.</td></tr>
            )}
            {filtered.map((u) => (
              <tr key={u.id} className="border-b border-black/5 hover:bg-neutral-50">
                <td className="px-4 py-3 font-mono-num">{fmtDate(u.due_date)}</td>
                <td className="px-4 py-3">
                  <div className="font-medium">{u.title}</div>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className="label-caps">{PAYMETHOD[u.payment_method || "bank_transfer"]}</span>
                    {u.is_recurring && <span className="label-caps">· ↻ {RECUR[u.recurrence]}</span>}
                  </div>
                </td>
                <td className="px-4 py-3"><span className={`border px-2 py-0.5 text-xs ${u.kind === "payment" ? "border-rose-300 text-rose-700" : "border-emerald-300 text-emerald-700"}`}>{u.kind === "payment" ? "Ödeme" : "Tahsilat"}</span></td>
                <td className="px-4 py-3 text-neutral-600">{contacts.find(c => c.id === u.contact_id)?.name || "—"}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs uppercase tracking-widest ${u.status === "overdue" ? "text-rose-600" : u.status === "paid" ? "text-emerald-700" : "text-neutral-500"}`}>
                    {u.status === "overdue" ? "Gecikmiş" : u.status === "paid" ? "Ödendi" : "Bekliyor"}
                  </span>
                </td>
                <td className={`px-4 py-3 text-right font-mono-num font-semibold ${u.kind === "payment" ? "text-rose-600" : "text-emerald-700"}`}>{fmtTRY(u.amount)}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {u.status !== "paid" && (
                    <button data-testid={`upcoming-mark-paid-${u.id}`} onClick={() => markPaid(u.id)} className="p-1.5 hover:bg-emerald-50" title="Ödendi">
                      <CheckCircle2 size={16} className="text-emerald-700" />
                    </button>
                  )}
                  <button onClick={() => remove(u.id)} className="p-1.5"><Trash2 size={14} className="text-rose-600" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <UpcomingModal accounts={accounts} contacts={contacts}
                       onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />
      )}
    </div>
  );
}

function UpcomingModal({ accounts, contacts, onClose, onSaved }) {
  const [form, setForm] = useState({
    kind: "payment", title: "", amount: "", due_date: todayISO(),
    account_id: accounts[0]?.id || "", contact_id: "",
    category: "", payment_method: "bank_transfer",
    is_recurring: false, recurrence: "monthly", notes: "",
  });
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/upcoming", { ...form, amount: Number(form.amount) });
      toast.success("Eklendi"); onSaved();
    } catch { toast.error("Hata"); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-white border border-black w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b-2 border-black">
          <h2 className="font-display text-xl font-bold">Yeni Kayıt</h2>
          <button type="button" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-2">
            {[["payment", "Ödeme"], ["collection", "Tahsilat"]].map(([k, l]) => (
              <button key={k} type="button" onClick={() => setForm({ ...form, kind: k })}
                      data-testid={`kind-${k}`}
                      className={`border py-2 text-sm uppercase tracking-widest ${form.kind === k ? (k === "payment" ? "bg-rose-600 text-white border-rose-600" : "bg-emerald-700 text-white border-emerald-700") : "border-black/20"}`}>
                {l}
              </button>
            ))}
          </div>
          <F label="Başlık"><input required data-testid="up-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="input" /></F>
          <div className="grid grid-cols-2 gap-3">
            <F label="Tutar (₺)"><input required type="number" step="0.01" data-testid="up-amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="input font-mono-num" /></F>
            <F label="Vade"><input required type="date" data-testid="up-date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="input" /></F>
          </div>
          <F label="Hesap">
            <select data-testid="up-account" value={form.account_id} onChange={(e) => setForm({ ...form, account_id: e.target.value })} className="input bg-white">
              <option value="">—</option>
              {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </F>
          <F label="Cari">
            <select data-testid="up-contact" value={form.contact_id} onChange={(e) => setForm({ ...form, contact_id: e.target.value })} className="input bg-white">
              <option value="">—</option>
              {contacts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </F>
          <F label="Kategori"><input data-testid="up-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="input" placeholder="Kira, Fatura…" /></F>
          <F label="Ödeme Yöntemi">
            <div className="grid grid-cols-3 gap-1.5">
              {Object.entries(PAYMETHOD).map(([k, l]) => (
                <button key={k} type="button" data-testid={`up-method-${k}`}
                        onClick={() => setForm({ ...form, payment_method: k })}
                        className={`text-xs uppercase tracking-widest py-2 border ${
                          form.payment_method === k ? "bg-black text-white border-black" : "border-black/15 hover:border-black/40"
                        }`}>
                  {l}
                </button>
              ))}
            </div>
            {form.payment_method === "cheque" && (
              <div className="mt-2 text-[11px] text-neutral-600 border-l-2 border-black pl-3 leading-relaxed">
                <b>Çek ile {form.kind === "payment" ? "ödeme" : "tahsilat"}:</b> "Ödendi" işaretlendiğinde otomatik olarak bir çek kaydı oluşturulur.
                Çek portföyünden takip edip, tahsil edildiğinde para hareketi oluşur.
              </div>
            )}
          </F>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" data-testid="up-recurring" checked={form.is_recurring} onChange={(e) => setForm({ ...form, is_recurring: e.target.checked })} />
            Tekrar eden
          </label>
          {form.is_recurring && (
            <F label="Sıklık">
              <select value={form.recurrence} onChange={(e) => setForm({ ...form, recurrence: e.target.value })} className="input bg-white">
                {Object.entries(RECUR).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </F>
          )}
        </div>
        <div className="flex justify-end gap-2 p-5 border-t border-black/10 bg-neutral-50">
          <button type="button" onClick={onClose} className="border border-black/20 px-4 py-2 text-sm">Vazgeç</button>
          <button data-testid="upcoming-save-button" type="submit" className="brutal-btn bg-black text-white px-5 py-2 text-sm">Kaydet</button>
        </div>
        <style>{`.input{width:100%;border:1px solid rgba(0,0,0,0.2);padding:0.5rem 0.75rem;outline:none}`}</style>
      </form>
    </div>
  );
}
function F({ label, children }) { return <label className="block"><div className="label-caps mb-1.5">{label}</div>{children}</label>; }
